import { useReducedMotion } from 'framer-motion';
import type { Transition, Variants } from 'framer-motion';

// Keep this mirror aligned with config/design-system/intellio-design-system.json.
const motionTokens = {
  durations: {
    hover: '140ms',
    micro: '180ms',
    panel: '220ms',
    'route-enter': '180ms',
    'route-exit': '120ms',
    emphasis: '280ms',
  },
  easing: {
    enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
    exit: 'cubic-bezier(0.32, 0, 0.67, 0)',
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    view: 'ease-in-out',
  },
  distance: {
    micro: '4px',
    panel: '12px',
    route: '16px',
  },
  reducedMotion: {
    opacityDuration: '120ms',
  },
} as const;

const toSeconds = (raw: string): number => {
  const trimmed = String(raw).trim().toLowerCase();
  if (trimmed.endsWith('ms')) {
    return Number.parseFloat(trimmed) / 1000;
  }
  if (trimmed.endsWith('s')) {
    return Number.parseFloat(trimmed);
  }
  return Number.parseFloat(trimmed) || 0;
};

const toBezier = (raw: string): [number, number, number, number] | 'linear' | 'easeInOut' => {
  const trimmed = String(raw).trim();
  if (trimmed === 'linear') return 'linear';
  if (trimmed === 'ease-in-out') return 'easeInOut';
  const match = trimmed.match(/cubic-bezier\(([^)]+)\)/i);
  if (!match?.[1]) {
    return [0.2, 0, 0, 1];
  }
  const points = match[1]
    .split(',')
    .map((value) => Number.parseFloat(value.trim()))
    .filter((value) => Number.isFinite(value));
  if (points.length !== 4) {
    return [0.2, 0, 0, 1];
  }
  return [points[0]!, points[1]!, points[2]!, points[3]!];
};

export const ystMotionTokens = {
  duration: {
    hover: toSeconds(motionTokens.durations.hover),
    micro: toSeconds(motionTokens.durations.micro),
    panel: toSeconds(motionTokens.durations.panel),
    routeEnter: toSeconds(motionTokens.durations['route-enter']),
    routeExit: toSeconds(motionTokens.durations['route-exit']),
    emphasis: toSeconds(motionTokens.durations.emphasis),
    reducedOpacity: toSeconds(motionTokens.reducedMotion.opacityDuration),
  },
  distance: {
    micro: Number.parseFloat(motionTokens.distance.micro),
    panel: Number.parseFloat(motionTokens.distance.panel),
    route: Number.parseFloat(motionTokens.distance.route),
  },
  easing: {
    enter: toBezier(motionTokens.easing.enter),
    exit: toBezier(motionTokens.easing.exit),
    standard: toBezier(motionTokens.easing.standard),
    view: toBezier(motionTokens.easing.view),
  },
} as const;

export const useYstReducedMotion = (): boolean => Boolean(useReducedMotion());

export const buildDialogBackdropVariants = (reducedMotion: boolean): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: reducedMotion ? ystMotionTokens.duration.reducedOpacity : ystMotionTokens.duration.micro,
      ease: ystMotionTokens.easing.standard,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: reducedMotion ? ystMotionTokens.duration.reducedOpacity : ystMotionTokens.duration.routeExit,
      ease: ystMotionTokens.easing.exit,
    },
  },
});

export const buildDialogPanelVariants = (reducedMotion: boolean): Variants => ({
  hidden: reducedMotion
    ? { opacity: 0 }
    : { opacity: 0, y: ystMotionTokens.distance.panel, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: reducedMotion ? ystMotionTokens.duration.reducedOpacity : ystMotionTokens.duration.panel,
      ease: ystMotionTokens.easing.enter,
    },
  },
  exit: reducedMotion
    ? {
        opacity: 0,
        transition: {
          duration: ystMotionTokens.duration.reducedOpacity,
          ease: ystMotionTokens.easing.exit,
        },
      }
    : {
        opacity: 0,
        y: ystMotionTokens.distance.micro,
        scale: 0.99,
        transition: {
          duration: ystMotionTokens.duration.routeExit,
          ease: ystMotionTokens.easing.exit,
        },
      },
});

export const buildRouteRevealVariants = (reducedMotion: boolean): Variants => ({
  hidden: reducedMotion
    ? { opacity: 0 }
    : { opacity: 0, y: ystMotionTokens.distance.route },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: reducedMotion ? ystMotionTokens.duration.reducedOpacity : ystMotionTokens.duration.routeEnter,
      ease: ystMotionTokens.easing.enter,
    },
  },
  exit: reducedMotion
    ? {
        opacity: 0,
        transition: {
          duration: ystMotionTokens.duration.reducedOpacity,
          ease: ystMotionTokens.easing.exit,
        },
      }
    : {
        opacity: 0,
        y: ystMotionTokens.distance.micro,
        transition: {
          duration: ystMotionTokens.duration.routeExit,
          ease: ystMotionTokens.easing.exit,
        },
      },
});

export const ystPressTransition: Transition = {
  duration: ystMotionTokens.duration.hover,
  ease: ystMotionTokens.easing.standard,
};
